import { TCreateJobReqDto } from "@/api/services/main/job/dtos/create-job-req.dto";
import { TGetJobDto } from "@/api/services/main/job/dtos/get-job.dto";
import { DATE_FORMAT_FOR_API } from "@/constants/datetime";
import { TJobForm, TOnboardingSupport } from "@/features/job/types/JobForm";
import { dayjs } from "@/utils/dayjs";

const cleanArray = <T>(arr: (T | null | undefined)[] | null | undefined): T[] | null => {
  if (!arr) return null;

  const cleaned = arr.filter((item): item is T => {
    if (item == null) return false;
    if (typeof item === "string") return item.trim() !== "";
    if (typeof item === "object") return Object.keys(item as object).some((k) => (item as Record<string, unknown>)[k] != null);
    return true;
  });

  return cleaned.length > 0 ? cleaned : null;
};
const formatOnboardingSupportField = (data: string[] | TOnboardingSupport[] | null | undefined): TOnboardingSupport[] | null | undefined => {
  if (!data) return data;

  if (data.length > 0 && data.some((item) => typeof item === "object")) {
    return data as TOnboardingSupport[];
  }

  const oldData = data as string[];
  return [
    ...oldData.map(label => ({ label, checked: true })),
  ];
}

export const cleanObject = <T extends object>(obj: T): Partial<T> => {
  if (!obj || typeof obj !== "object") return {};

  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value === undefined || value === null) return acc;
    if (typeof value === "string" && value.trim() === "") return acc;
    if (Array.isArray(value) && value.length === 0) return acc;

    (acc as Partial<T>)[key as keyof T] = value;
    return acc;
  }, {} as Partial<T>);
};

export const convertJobFormDataReq = (data: TJobForm): TCreateJobReqDto => {
  const raw = {
    companyId: Number(data.companyId),
    companySpecificIndustryId: data.companySpecificIndustryId ? Number(data.companySpecificIndustryId) : null,
    businessClassificationId: data.businessClassificationId ? Number(data.businessClassificationId) : null,
    recruiterId: Number(data.recruiterId),
    recruitmentSourceIds: cleanArray(data.recruitmentSourceIds),
    status: data.status,
    tag: cleanArray(data.tag),

    startDate: data.startDate ? dayjs(data.startDate).format(DATE_FORMAT_FOR_API) : null,
    endDate: data.endDate ? dayjs(data.endDate).format(DATE_FORMAT_FOR_API) : null,

    statusMemo: data.statusMemo ?? null,
    managementNumber: data.managementNumber ?? null,
    jobName: data.jobName,

    companySpecificBusinessClassificationIds: cleanArray(data.companySpecificBusinessClassificationIds),

    recruitmentNumber: data.recruitmentNumber ?? null,
    referralFee: data.referralFee ?? null,
    referralFeeCost: data.referralFeeCost ?? null,
    clientType: cleanArray(data.clientType),
    recruiterNote: data.recruiterNote ?? null,
    businessContent: data.businessContent ?? null,

    employmentType: data.employmentType ?? null,
    salaryType: data.salaryType ?? null,
    baseSalary: data.baseSalary ?? null,
    totalAmount: data.totalAmount ?? null,
    totalAmountBreakdown: data.totalAmountBreakdown ?? null,
    netSalaryEstimate: data.netSalaryEstimate ?? null,
    salaryDeductions: data.salaryDeductions ?? null,
    hourlyWage: data.hourlyWage ?? null,

    otherAllowanceAmount: data.otherAllowanceAmount ?? null,
    otherAllowanceDescription: data.otherAllowanceDescription ?? null,
    overtimeDescription: data.overtimeDescription ?? null,

    bonus: data.bonus ?? null,
    bonusInfo: data.bonusInfo ?? null,
    salaryRaise: data.salaryRaise ?? null,
    salaryRaiseTime: data.salaryRaiseTime ?? null,

    workplace: data.workplace ?? null,
    workplaceDetail: data.workplaceDetail ?? null,
    jobWorkingTimes: cleanArray(data.jobWorkingTimes),
    regularHolidays: data.regularHolidays ?? null,
    irregularHolidays: data.irregularHolidays ?? null,
    totalAnnualHolidays: data.totalAnnualHolidays ?? null,

    japaneseLevelType: data.japaneseLevelType ?? null,
    japaneseLevelOther: data.japaneseLevelOther ?? null,
    skillsExperience: data.skillsExperience ?? null,
    otherApplicationRequirements: data.otherApplicationRequirements ?? null,
    ageLimit: data.ageLimit ?? null,

    nationalityIds: cleanArray(data.nationalityIds),
    recruitmentLocationType: data.recruitmentLocationType ?? null,
    desiredHiringPeriod: data.desiredHiringPeriod ?? null,
    gender: data.gender ?? null,
    residenceStatusIds: cleanArray(data.residenceStatusIds),
    specifiedSkillPassed: cleanArray(data.specifiedSkillPassed),

    jobDocumentUrl: data.jobDocumentUrl ?? null,
    jobDocumentFile: data.jobDocumentFile ?? null,
    jobDocumentFilePath: data.jobDocumentFilePath ?? null,
    selectionProcess: data.selectionProcess ?? null,
    companyAccommodation: data.companyAccommodation ?? null,
    specifiedSkillNo2: data.specifiedSkillNo2 ?? null,
    otherBenefits: data.otherBenefits ?? null,
    insuranceStatus: data.insuranceStatus ?? null,
    memo: data.memo ?? null,
    jobMemo: data.jobMemo ?? null,
    foreignEmployees: data.foreignEmployees ?? null,
    onboardingSupport: cleanArray(data.onboardingSupport),
    relatedFiles: data.relatedFiles ?? null,
    relatedFilePaths: JSON.stringify(data.relatedFilePaths) ?? null,
    relatedFileTitles: cleanArray(data.relatedFileTitles) ?? null,
  };

  const payload = raw as TCreateJobReqDto;
  return payload;
};

export const convertResToFormData = (data: TGetJobDto): Partial<TJobForm> | undefined => {
  if (!data) return undefined;

  const response = {
    companyId: data.companyId,
    companySpecificIndustryId: data.companySpecificIndustryId ? Number(data.companySpecificIndustryId) : null,
    businessClassificationId: data.businessClassificationId ? Number(data.businessClassificationId) : null,
    recruiterId: Number(data.recruiterId),
    recruitmentSourceIds: data.recruitmentSourceIds ? data.recruitmentSourceIds.map((item) => item.toString()) : null,
    status: data.status,
    tag: data.tag ?? null,

    startDate: data.startDate ? dayjs(data.startDate) : null,
    endDate: data.endDate ? dayjs(data.endDate) : null,
    statusMemo: data.statusMemo ?? null,
    managementNumber: data.managementNumber ?? null,
    jobName: data.jobName,
    companySpecificBusinessClassificationIds: data.companySpecificBusinessClassificationIds ? data.companySpecificBusinessClassificationIds.map((item) => item.toString()) : null,

    recruitmentNumber: data.recruitmentNumber ?? null,
    referralFee: data.referralFee ?? null,
    referralFeeCost: data.referralFeeCost ?? null,
    clientType: data.clientType ?? null,
    recruiterNote: data.recruiterNote ?? null,
    businessContent: data.businessContent ?? null,
    employmentType: data.employmentType ?? null,
    salaryType: data.salaryType ?? null,
    baseSalary: data.baseSalary ?? null,
    totalAmount: data.totalAmount ?? null,
    totalAmountBreakdown: data.totalAmountBreakdown ?? null,
    netSalaryEstimate: data.netSalaryEstimate ?? null,
    salaryDeductions: data.salaryDeductions ?? null,
    hourlyWage: data.hourlyWage ?? null,
    otherAllowanceAmount: data.otherAllowanceAmount ?? null,
    otherAllowanceDescription: data.otherAllowanceDescription ?? null,
    overtimeDescription: data.overtimeDescription ?? null,
    bonus: data.bonus ?? null,
    bonusInfo: data.bonusInfo ?? null,
    salaryRaise: data.salaryRaise ?? null,
    salaryRaiseTime: data.salaryRaiseTime ?? null,
    workplace: data.workplace ?? null,
    workplaceDetail: data.workplaceDetail ?? null,
    jobWorkingTimes:
      data?.jobWorkingTimes && data?.jobWorkingTimes.length > 0
        ? data.jobWorkingTimes.map((workingTime) => ({
            id: workingTime.id,
            workingStartHour: workingTime.workingStartHour,
            workingStartMinute: workingTime.workingStartMinute,
            workingEndHour: workingTime.workingEndHour,
            workingEndMinute: workingTime.workingEndMinute,
          }))
        : [initJobWorkingTimeForm],
    regularHolidays: data.regularHolidays ?? null,
    irregularHolidays: data.irregularHolidays ?? null,
    totalAnnualHolidays: data.totalAnnualHolidays ?? null,
    japaneseLevelType: data.japaneseLevelType ?? null,
    japaneseLevelOther: data.japaneseLevelOther ?? null,
    skillsExperience: data.skillsExperience ?? null,
    otherApplicationRequirements: data.otherApplicationRequirements ?? null,
    ageLimit: data.ageLimit ?? null,
    nationalityIds: data.nationalityIds ? data.nationalityIds.map((item) => item.toString()) : null,
    recruitmentLocationType: data.recruitmentLocationType ?? null,
    desiredHiringPeriod: data.desiredHiringPeriod ?? null,
    gender: data.gender ?? null,
    residenceStatusIds: data.residenceStatusIds ? data.residenceStatusIds.map((item) => item.toString()) : null,
    specifiedSkillPassed: data.specifiedSkillPassed ?? null,

    jobDocumentUrl: data.jobDocumentUrl ?? null,
    jobDocumentFilePath: data.jobDocumentFilePath ?? null,
    selectionProcess: data.selectionProcess ?? null,
    companyAccommodation: data.companyAccommodation ?? null,
    specifiedSkillNo2: data.specifiedSkillNo2 ?? null,
    otherBenefits: data.otherBenefits ?? null,
    insuranceStatus: data.insuranceStatus ?? null,
    memo: data.memo ?? null,
    jobMemo: data.jobMemo ?? null,
    foreignEmployees: data.foreignEmployees ?? null,
    onboardingSupport: formatOnboardingSupportField(data.onboardingSupport) ?? null,
    company: data.company ?? null,
    businessClassification: data.businessClassification ?? null,
    companySpecificIndustry: data.companySpecificIndustry ?? null,
    recruiter: data.recruiter ?? null,
    jobUploadFiles: data.jobUploadFiles,
    nationalities: data.nationalities,
    cases: data.cases,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };

  return response;
};

export const initJobWorkingTimeForm = {
  workingStartHour: null,
  workingStartMinute: null,
  workingEndHour: null,
  workingEndMinute: null,
};


